<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { messageStamp } from 'shared/helpers/timeHelper';
import FlowsAPI from 'dashboard/api/flows';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const SAMPLE_SCHEMA = {
  start_node_id: 'node_start',
  nodes: [
    {
      id: 'node_start',
      type: 'whatsapp_list',
      title: 'Menu inicial',
      config: {
        body: 'Olá! Seja bem-vindo 🚀\nComo posso te ajudar hoje?',
        footer: 'Atendimento automático',
        button_text: 'Escolher opção',
        sections: [
          {
            title: 'Atendimento',
            rows: [
              { id: 'nova_compra', title: 'Nova compra', description: 'Quero conhecer os pacotes', target_node_id: 'node_nova_compra' },
              { id: 'humano', title: 'Falar com atendente', description: 'Quero atendimento humano', target_node_id: 'node_handoff' },
            ],
          },
        ],
        fallback_target_node_id: 'node_fallback',
      },
    },
    {
      id: 'node_nova_compra',
      type: 'message',
      title: 'Nova compra',
      config: { text: 'Perfeito! Em instantes um atendente continua com você.' },
      next_node_id: 'node_handoff',
    },
    {
      id: 'node_handoff',
      type: 'handoff',
      title: 'Transferir para humano',
      config: { message: 'Certo! Vou te encaminhar para um atendente agora.', labels: ['humano'] },
    },
    {
      id: 'node_fallback',
      type: 'message',
      title: 'Fallback',
      config: { text: 'Não consegui identificar sua escolha. Por favor, selecione uma opção da lista.' },
      next_node_id: 'node_start',
    },
  ],
};

const TRIGGERS = ['new_conversation', 'first_incoming_message', 'keyword', 'manual'];
const TRIGGER_ICON = {
  new_conversation: 'i-lucide-message-circle-plus',
  first_incoming_message: 'i-lucide-message-square-text',
  keyword: 'i-lucide-hash',
  manual: 'i-lucide-hand',
};
const WHATSAPP_BLOCK_TYPES = ['whatsapp_list', 'buttons', 'media'];

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const router = useRouter();

const flows = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showExecutionsModal = ref(false);
const showActivateConfirm = ref(false);
const executions = ref([]);
const selectedFlow = ref(null);
const flowToActivate = ref(null);
const activeTab = ref('config');
const keywordInput = ref('');

const defaultForm = () => ({
  id: null,
  name: '',
  description: '',
  status: 'draft',
  applies_to_all: true,
  inbox_ids: [],
  trigger_type: 'first_incoming_message',
  keywords: [],
  match_mode: 'contains',
  case_sensitive: false,
  schemaText: '',
  nodeCount: 0,
  updated_at: null,
});
const form = ref(defaultForm());
const schemaError = ref('');

const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);
const whatsappInboxes = computed(() =>
  inboxes.value.filter(i => i.channel_type === 'Channel::Whatsapp')
);
const isWhatsapp = inbox => inbox.channel_type === 'Channel::Whatsapp';

const openBuilder = flow => {
  router.push({ name: 'flows_builder', params: { flowId: flow.id } });
};

const inboxSummary = flow => {
  if (flow.applies_to_all_inboxes) return t('FLOWS.CARD.ALL_INBOXES');
  const names = (flow.inboxes || []).map(i => i.name);
  return names.length ? names.join(', ') : t('FLOWS.CARD.ALL_INBOXES');
};
const triggerLabel = trigger => t(`FLOWS.FORM.TRIGGER.${(trigger || 'manual').toUpperCase()}`);
const statusLabel = status => t(`FLOWS.STATUS.${(status || 'draft').toUpperCase()}`);
const formatDate = value =>
  value ? messageStamp(new Date(value).getTime() / 1000, 'dd/MM/yyyy HH:mm') : '-';

// count nodes in a schema safely
const nodeCount = flow => (flow.draft_schema?.nodes || []).length;

// does the current form's schema contain WhatsApp-only blocks?
const formHasWhatsappBlocks = computed(() => {
  try {
    const parsed = JSON.parse(form.value.schemaText || '{}');
    return (parsed.nodes || []).some(n => WHATSAPP_BLOCK_TYPES.includes(n.type));
  } catch {
    return false;
  }
});
// warn if a non-WhatsApp inbox is selected while flow uses WhatsApp blocks
const nonWhatsappSelected = computed(() =>
  !form.value.applies_to_all &&
  inboxes.value.some(i => form.value.inbox_ids.includes(i.id) && !isWhatsapp(i))
);

const fetchFlows = async () => {
  isLoading.value = true;
  try {
    const { data } = await FlowsAPI.get();
    flows.value = data.payload || [];
  } catch (error) {
    useAlert(t('FLOWS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openCreateModal = () => {
  form.value = { ...defaultForm(), schemaText: JSON.stringify(SAMPLE_SCHEMA, null, 2) };
  keywordInput.value = '';
  schemaError.value = '';
  activeTab.value = 'config';
  showEditModal.value = true;
};

const openEditModal = flow => {
  const cfg = flow.trigger_config || {};
  form.value = {
    id: flow.id,
    name: flow.name,
    description: flow.description || '',
    status: flow.status,
    applies_to_all: flow.applies_to_all_inboxes !== false && !(flow.inbox_ids || []).length,
    inbox_ids: [...(flow.inbox_ids || [])],
    trigger_type: flow.trigger_type,
    keywords: [...(cfg.keywords || [])],
    match_mode: cfg.match_mode || 'contains',
    case_sensitive: !!cfg.case_sensitive,
    schemaText: JSON.stringify(flow.draft_schema || {}, null, 2),
    nodeCount: nodeCount(flow),
    updated_at: flow.updated_at,
  };
  keywordInput.value = '';
  schemaError.value = '';
  activeTab.value = 'config';
  showEditModal.value = true;
};

const toggleInbox = id => {
  const idx = form.value.inbox_ids.indexOf(id);
  if (idx === -1) form.value.inbox_ids.push(id);
  else form.value.inbox_ids.splice(idx, 1);
};

const addKeyword = () => {
  const raw = keywordInput.value.split(',').map(k => k.trim()).filter(Boolean);
  raw.forEach(k => {
    if (!form.value.keywords.includes(k)) form.value.keywords.push(k);
  });
  keywordInput.value = '';
};
const removeKeyword = kw => {
  form.value.keywords = form.value.keywords.filter(k => k !== kw);
};

const insertSample = () => {
  form.value.schemaText = JSON.stringify(SAMPLE_SCHEMA, null, 2);
  schemaError.value = '';
};
const copyJson = async () => {
  try {
    await navigator.clipboard.writeText(form.value.schemaText);
    useAlert(t('FLOWS.JSON_ADV.COPIED'));
  } catch {
    useAlert(t('FLOWS.API.ERROR'));
  }
};

const buildPayload = () => {
  const parsed = JSON.parse(form.value.schemaText || '{}');
  return {
    flow: {
      name: form.value.name,
      description: form.value.description,
      inbox_ids: form.value.applies_to_all ? [] : form.value.inbox_ids,
      trigger_type: form.value.trigger_type,
      trigger_config:
        form.value.trigger_type === 'keyword'
          ? {
              keywords: form.value.keywords,
              match_mode: form.value.match_mode,
              case_sensitive: form.value.case_sensitive,
            }
          : {},
      draft_schema: parsed,
    },
  };
};

// save; returns the saved flow id (for "save and open builder")
const saveFlow = async (openAfter = false) => {
  let payload;
  try {
    payload = buildPayload();
    schemaError.value = '';
  } catch {
    activeTab.value = 'json';
    schemaError.value = t('FLOWS.FORM.SCHEMA.INVALID_JSON');
    return;
  }
  isSaving.value = true;
  try {
    let saved;
    if (form.value.id) {
      const { data } = await FlowsAPI.update(form.value.id, payload);
      saved = data;
    } else {
      const { data } = await FlowsAPI.create(payload);
      saved = data;
    }
    useAlert(t('FLOWS.API.SAVE_SUCCESS'));
    showEditModal.value = false;
    await fetchFlows();
    if (openAfter && saved?.id) openBuilder(saved);
  } catch (error) {
    useAlert(error?.response?.data?.errors?.join(', ') || t('FLOWS.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const askActivate = flow => {
  flowToActivate.value = flow;
  showActivateConfirm.value = true;
};
const confirmActivate = async () => {
  const flow = flowToActivate.value;
  showActivateConfirm.value = false;
  try {
    await FlowsAPI.activate(flow.id);
    useAlert(t('FLOWS.API.ACTIVATE_SUCCESS'));
    await fetchFlows();
  } catch (error) {
    useAlert(error?.response?.data?.errors?.join(', ') || t('FLOWS.API.ERROR'));
  }
};
const deactivateFlow = async flow => {
  try {
    await FlowsAPI.deactivate(flow.id);
    useAlert(t('FLOWS.API.DEACTIVATE_SUCCESS'));
    await fetchFlows();
  } catch (error) {
    useAlert(t('FLOWS.API.ERROR'));
  }
};

const openDeleteModal = flow => {
  selectedFlow.value = flow;
  showDeleteModal.value = true;
};
const confirmDelete = async () => {
  try {
    await FlowsAPI.delete(selectedFlow.value.id);
    useAlert(t('FLOWS.API.DELETE_SUCCESS'));
    showDeleteModal.value = false;
    await fetchFlows();
  } catch {
    useAlert(t('FLOWS.API.ERROR'));
  }
};

const openExecutions = async flow => {
  selectedFlow.value = flow;
  executions.value = [];
  showExecutionsModal.value = true;
  try {
    const { data } = await FlowsAPI.executions(flow.id);
    executions.value = data.payload || [];
  } catch {
    useAlert(t('FLOWS.API.ERROR'));
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchFlows();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('FLOWS.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('FLOWS.HEADER')"
        :description="$t('FLOWS.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            v-if="flows.length"
            icon="i-lucide-circle-plus"
            :label="$t('FLOWS.NEW_FLOW')"
            @click="openCreateModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <!-- EMPTY: no WhatsApp inbox -->
      <div
        v-if="!whatsappInboxes.length"
        class="flex flex-col items-center justify-center gap-3 py-16 text-center"
      >
        <span class="i-lucide-message-square-off text-4xl text-n-slate-9" />
        <h3 class="text-lg font-medium text-n-slate-12">{{ $t('FLOWS.EMPTY.NO_WHATSAPP_TITLE') }}</h3>
        <p class="max-w-md text-sm text-n-slate-11">{{ $t('FLOWS.EMPTY.NO_WHATSAPP_BODY') }}</p>
      </div>

      <!-- EMPTY: no flows -->
      <div
        v-else-if="!flows.length"
        class="flex flex-col items-center justify-center gap-3 py-16 text-center"
      >
        <span class="i-lucide-workflow text-4xl text-n-slate-9" />
        <h3 class="text-lg font-medium text-n-slate-12">{{ $t('FLOWS.EMPTY.NO_FLOWS_TITLE') }}</h3>
        <p class="max-w-md text-sm text-n-slate-11">{{ $t('FLOWS.EMPTY.NO_FLOWS_BODY') }}</p>
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('FLOWS.EMPTY.CREATE_FIRST')"
          @click="openCreateModal"
        />
      </div>

      <!-- CARDS -->
      <div v-else class="flex flex-col gap-3">
        <div
          v-for="flow in flows"
          :key="flow.id"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 hover:border-n-slate-6 transition-colors"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="font-medium text-n-slate-12 truncate">{{ flow.name }}</h3>
                <span
                  class="px-2 py-0.5 text-xs rounded-md shrink-0"
                  :class="flow.status === 'active'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : flow.status === 'inactive'
                      ? 'bg-n-amber-3 text-n-amber-11'
                      : 'bg-n-slate-3 text-n-slate-11'"
                >
                  {{ statusLabel(flow.status) }}
                </span>
              </div>
              <p v-if="flow.description" class="mt-0.5 text-sm text-n-slate-11 truncate">
                {{ flow.description }}
              </p>
            </div>
          </div>

          <div class="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-n-slate-11">
            <span class="flex items-center gap-1.5">
              <span :class="TRIGGER_ICON[flow.trigger_type]" class="text-n-slate-10" />
              {{ $t('FLOWS.CARD.TRIGGER') }}: <span class="text-n-slate-12">{{ triggerLabel(flow.trigger_type) }}</span>
            </span>
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-inbox text-n-slate-10" />
              {{ $t('FLOWS.CARD.INBOXES') }}: <span class="text-n-slate-12">{{ inboxSummary(flow) }}</span>
            </span>
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-clock text-n-slate-10" />
              {{ $t('FLOWS.CARD.UPDATED') }}: {{ formatDate(flow.updated_at) }}
            </span>
          </div>

          <div class="mt-4 flex flex-wrap items-center gap-2">
            <NextButton
              icon="i-lucide-workflow"
              :label="$t('FLOWS.CARD.OPEN_BUILDER')"
              sm
              @click="openBuilder(flow)"
            />
            <NextButton
              icon="i-lucide-settings-2"
              :label="$t('FLOWS.CARD.SETTINGS')"
              slate
              faded
              sm
              @click="openEditModal(flow)"
            />
            <NextButton
              v-if="flow.status === 'active'"
              icon="i-lucide-pause"
              :label="$t('FLOWS.CARD.DEACTIVATE')"
              amber
              faded
              sm
              @click="deactivateFlow(flow)"
            />
            <NextButton
              v-else
              icon="i-lucide-play"
              :label="$t('FLOWS.CARD.ACTIVATE')"
              teal
              faded
              sm
              @click="askActivate(flow)"
            />
            <NextButton
              icon="i-lucide-list"
              :label="$t('FLOWS.CARD.EXECUTIONS')"
              slate
              faded
              sm
              @click="openExecutions(flow)"
            />
            <NextButton
              v-tooltip.top="$t('FLOWS.ACTIONS.DELETE')"
              icon="i-lucide-trash-2"
              ruby
              faded
              sm
              class="ltr:ml-auto rtl:mr-auto"
              @click="openDeleteModal(flow)"
            />
          </div>
        </div>
      </div>

      <!-- CONFIG MODAL WITH TABS -->
      <woot-modal v-model:show="showEditModal" :on-close="() => (showEditModal = false)" size="medium">
        <div class="flex flex-col h-auto max-h-[85vh] overflow-hidden">
          <woot-modal-header
            :header-title="form.id ? $t('FLOWS.CONFIG.EDIT_TITLE') : $t('FLOWS.CONFIG.NEW_TITLE')"
          />

          <!-- tabs -->
          <div class="flex gap-1 border-b border-n-weak px-8 pt-3">
            <button
              v-for="tab in ['config', 'trigger', 'inboxes', 'json']"
              :key="tab"
              type="button"
              class="px-3 py-2 text-sm border-b-2 -mb-px transition-colors"
              :class="activeTab === tab
                ? 'border-n-brand text-n-slate-12 font-medium'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
              @click="activeTab = tab"
            >
              {{ $t(`FLOWS.TABS.${tab.toUpperCase()}`) }}
            </button>
          </div>

          <form class="flex flex-col w-full gap-4 px-8 py-5 overflow-auto" @submit.prevent="saveFlow(false)">
            <!-- TAB: CONFIG -->
            <template v-if="activeTab === 'config'">
              <woot-input
                v-model="form.name"
                :label="$t('FLOWS.FORM.NAME.LABEL')"
                :placeholder="$t('FLOWS.FORM.NAME.PLACEHOLDER')"
              />
              <woot-input
                v-model="form.description"
                :label="$t('FLOWS.FORM.DESCRIPTION.LABEL')"
                :placeholder="$t('FLOWS.FORM.DESCRIPTION.PLACEHOLDER')"
              />
              <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3 text-sm flex flex-col gap-1.5">
                <span class="font-medium text-n-slate-12">{{ $t('FLOWS.CONFIG.SUMMARY') }}</span>
                <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('FLOWS.CARD.TRIGGER') }}</span><span class="text-n-slate-12">{{ triggerLabel(form.trigger_type) }}</span></div>
                <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('FLOWS.CARD.INBOXES') }}</span><span class="text-n-slate-12">{{ form.applies_to_all ? $t('FLOWS.CARD.ALL_INBOXES') : form.inbox_ids.length }}</span></div>
                <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('FLOWS.CONFIG.NODES') }}</span><span class="text-n-slate-12">{{ form.nodeCount }}</span></div>
                <div v-if="form.updated_at" class="flex justify-between"><span class="text-n-slate-11">{{ $t('FLOWS.CARD.UPDATED') }}</span><span class="text-n-slate-12">{{ formatDate(form.updated_at) }}</span></div>
              </div>
              <div class="rounded-lg border border-n-amber-5 bg-n-amber-2 p-3 text-xs text-n-amber-12 flex gap-2">
                <span class="i-lucide-triangle-alert shrink-0 mt-0.5" />
                {{ $t('FLOWS.WARNINGS.N8N') }}
              </div>
            </template>

            <!-- TAB: TRIGGER -->
            <template v-if="activeTab === 'trigger'">
              <div class="flex flex-col gap-2">
                <label
                  v-for="trg in TRIGGERS"
                  :key="trg"
                  class="flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors"
                  :class="form.trigger_type === trg ? 'border-n-brand bg-n-brand/5' : 'border-n-weak hover:border-n-slate-6'"
                >
                  <input v-model="form.trigger_type" type="radio" :value="trg" class="!mb-0 mt-0.5" />
                  <span class="flex flex-col gap-0.5">
                    <span class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12">
                      <span :class="TRIGGER_ICON[trg]" class="text-n-slate-10" />
                      {{ $t(`FLOWS.FORM.TRIGGER.${trg.toUpperCase()}`) }}
                    </span>
                    <span class="text-xs text-n-slate-11">{{ $t(`FLOWS.TRIGGER_DESC.${trg.toUpperCase()}`) }}</span>
                  </span>
                </label>
              </div>

              <template v-if="form.trigger_type === 'keyword'">
                <div class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-n-slate-12">{{ $t('FLOWS.FORM.TRIGGER.KEYWORDS_LABEL') }}</span>
                  <div class="flex flex-wrap gap-1.5 rounded-md border border-n-weak p-2 min-h-10">
                    <span
                      v-for="kw in form.keywords"
                      :key="kw"
                      class="flex items-center gap-1 rounded-full bg-n-brand/10 text-n-brand px-2 py-0.5 text-xs"
                    >
                      {{ kw }}
                      <button type="button" class="i-lucide-x text-[10px]" @click="removeKeyword(kw)" />
                    </span>
                    <input
                      v-model="keywordInput"
                      type="text"
                      class="!mb-0 !border-0 !p-0 flex-1 min-w-24 text-sm !bg-transparent"
                      :placeholder="$t('FLOWS.FORM.TRIGGER.KEYWORDS_PLACEHOLDER')"
                      @keydown.enter.prevent="addKeyword"
                      @keydown.,.prevent="addKeyword"
                      @blur="addKeyword"
                    />
                  </div>
                </div>
                <label>
                  {{ $t('FLOWS.FORM.TRIGGER.MATCH_MODE') }}
                  <select v-model="form.match_mode">
                    <option value="contains">{{ $t('FLOWS.FORM.TRIGGER.MODE_CONTAINS') }}</option>
                    <option value="equals">{{ $t('FLOWS.FORM.TRIGGER.MODE_EQUALS') }}</option>
                    <option value="starts_with">{{ $t('FLOWS.FORM.TRIGGER.MODE_STARTS_WITH') }}</option>
                  </select>
                </label>
                <label class="flex items-center gap-2 text-sm text-n-slate-11">
                  <input v-model="form.case_sensitive" type="checkbox" class="!mb-0" />
                  {{ $t('FLOWS.FORM.TRIGGER.CASE_SENSITIVE') }}
                </label>
                <p v-if="form.keywords.length" class="text-xs text-n-slate-10">
                  {{ $t('FLOWS.TRIGGER_DESC.KEYWORD_PREVIEW', { list: form.keywords.join(', ') }) }}
                </p>
              </template>
              <p v-if="form.trigger_type === 'manual'" class="text-xs text-n-amber-11 flex items-center gap-1.5">
                <span class="i-lucide-info" /> {{ $t('FLOWS.FORM.TRIGGER.MANUAL_HINT') }}
              </p>
            </template>

            <!-- TAB: INBOXES -->
            <template v-if="activeTab === 'inboxes'">
              <label class="flex items-center gap-2 text-sm text-n-slate-12 rounded-lg border border-n-weak p-3">
                <input v-model="form.applies_to_all" type="checkbox" class="!mb-0" />
                <span>
                  <span class="font-medium">{{ $t('FLOWS.FORM.INBOX.ALL') }}</span>
                  <span class="block text-xs text-n-slate-11">{{ $t('FLOWS.WARNINGS.GLOBAL') }}</span>
                </span>
              </label>
              <div v-if="!form.applies_to_all" class="flex flex-col gap-1 max-h-56 overflow-auto rounded-lg border border-n-weak p-2">
                <label
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  class="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm hover:bg-n-alpha-1 cursor-pointer"
                >
                  <span class="flex items-center gap-2 text-n-slate-12">
                    <input type="checkbox" class="!mb-0" :checked="form.inbox_ids.includes(inbox.id)" @change="toggleInbox(inbox.id)" />
                    {{ inbox.name }}
                  </span>
                  <span
                    class="text-xs px-1.5 py-0.5 rounded"
                    :class="isWhatsapp(inbox) ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-amber-3 text-n-amber-11'"
                  >
                    {{ isWhatsapp(inbox) ? 'WhatsApp' : $t('FLOWS.FORM.INBOX.NOT_WHATSAPP') }}
                  </span>
                </label>
                <p v-if="!inboxes.length" class="text-xs text-n-slate-10 p-2">{{ $t('FLOWS.FORM.INBOX.EMPTY') }}</p>
              </div>
              <div v-if="nonWhatsappSelected && formHasWhatsappBlocks" class="rounded-lg border border-n-amber-5 bg-n-amber-2 p-3 text-xs text-n-amber-12 flex gap-2">
                <span class="i-lucide-triangle-alert shrink-0 mt-0.5" />
                {{ $t('FLOWS.WARNINGS.NON_WHATSAPP_BLOCKS') }}
              </div>
            </template>

            <!-- TAB: JSON -->
            <template v-if="activeTab === 'json'">
              <div class="rounded-lg border border-n-amber-5 bg-n-amber-2 p-3 text-xs text-n-amber-12 flex gap-2">
                <span class="i-lucide-triangle-alert shrink-0 mt-0.5" />
                {{ $t('FLOWS.JSON_ADV.WARNING') }}
              </div>
              <textarea
                v-model="form.schemaText"
                rows="14"
                class="font-mono text-xs !mb-0"
                spellcheck="false"
              />
              <span v-if="schemaError" class="text-xs text-n-ruby-11">{{ schemaError }}</span>
              <div class="flex gap-2">
                <NextButton type="button" slate faded sm :label="$t('FLOWS.JSON_ADV.COPY')" @click="copyJson" />
                <NextButton type="button" slate faded sm :label="$t('FLOWS.FORM.SCHEMA.INSERT_SAMPLE')" @click="insertSample" />
              </div>
            </template>

            <!-- footer -->
            <div class="flex flex-row justify-end w-full gap-2 pt-2 border-t border-n-weak mt-1">
              <NextButton
                type="button"
                slate
                faded
                :label="$t('FLOWS.FORM.CANCEL')"
                @click="showEditModal = false"
              />
              <NextButton
                v-if="!form.id"
                type="button"
                slate
                :label="$t('FLOWS.CONFIG.SAVE_DRAFT')"
                :is-loading="isSaving"
                @click="saveFlow(false)"
              />
              <NextButton
                v-if="!form.id"
                type="button"
                icon="i-lucide-workflow"
                :label="$t('FLOWS.CONFIG.SAVE_OPEN_BUILDER')"
                :is-loading="isSaving"
                @click="saveFlow(true)"
              />
              <NextButton
                v-if="form.id"
                type="submit"
                :label="$t('FLOWS.FORM.SAVE')"
                :is-loading="isSaving"
              />
            </div>
          </form>
        </div>
      </woot-modal>

      <!-- ACTIVATE CONFIRMATION -->
      <woot-modal v-model:show="showActivateConfirm" :on-close="() => (showActivateConfirm = false)" size="small">
        <div v-if="flowToActivate" class="flex flex-col gap-4 p-8">
          <h3 class="text-lg font-medium text-n-slate-12">{{ $t('FLOWS.CONFIRM.ACTIVATE_TITLE') }}</h3>
          <p class="text-sm text-n-slate-11">{{ $t('FLOWS.CONFIRM.ACTIVATE_BODY') }}</p>
          <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3 text-sm flex flex-col gap-1.5">
            <div><span class="text-n-slate-11">{{ $t('FLOWS.CARD.INBOXES') }}: </span><span class="text-n-slate-12">{{ inboxSummary(flowToActivate) }}</span></div>
            <div><span class="text-n-slate-11">{{ $t('FLOWS.CARD.TRIGGER') }}: </span><span class="text-n-slate-12">{{ triggerLabel(flowToActivate.trigger_type) }}</span></div>
          </div>
          <div class="rounded-lg border border-n-amber-5 bg-n-amber-2 p-3 text-xs text-n-amber-12">
            {{ $t('FLOWS.CONFIRM.ACTIVATE_N8N') }}
          </div>
          <div class="flex justify-end gap-2">
            <NextButton slate faded :label="$t('FLOWS.FORM.CANCEL')" @click="showActivateConfirm = false" />
            <NextButton teal :label="$t('FLOWS.CARD.ACTIVATE')" @click="confirmActivate" />
          </div>
        </div>
      </woot-modal>

      <!-- EXECUTIONS -->
      <woot-modal v-model:show="showExecutionsModal" :on-close="() => (showExecutionsModal = false)" size="medium">
        <div class="flex flex-col h-auto overflow-auto">
          <woot-modal-header :header-title="$t('FLOWS.EXECUTIONS.TITLE')" />
          <div class="px-8 pt-4 pb-8">
            <p v-if="!executions.length" class="text-n-slate-11">{{ $t('FLOWS.EXECUTIONS.EMPTY') }}</p>
            <table v-else class="min-w-full divide-y divide-n-weak text-sm">
              <thead>
                <tr>
                  <th class="py-2 text-left">{{ $t('FLOWS.EXECUTIONS.CONVERSATION') }}</th>
                  <th class="py-2 text-left">{{ $t('FLOWS.EXECUTIONS.STATUS') }}</th>
                  <th class="py-2 text-left">{{ $t('FLOWS.EXECUTIONS.CURRENT_NODE') }}</th>
                  <th class="py-2 text-left">{{ $t('FLOWS.EXECUTIONS.STARTED_AT') }}</th>
                  <th class="py-2 text-left">{{ $t('FLOWS.EXECUTIONS.ERROR') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="execution in executions" :key="execution.id">
                  <td class="py-2">#{{ execution.conversation_display_id }}</td>
                  <td class="py-2">{{ execution.status }} ({{ execution.steps.length }} {{ $t('FLOWS.EXECUTIONS.STEPS') }})</td>
                  <td class="py-2">{{ execution.current_node_id || '-' }}</td>
                  <td class="py-2">{{ formatDate(execution.started_at) }}</td>
                  <td class="py-2 text-n-ruby-11">{{ execution.last_error || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </woot-modal>

      <woot-delete-modal
        v-if="showDeleteModal"
        v-model:show="showDeleteModal"
        :on-close="() => (showDeleteModal = false)"
        :on-confirm="confirmDelete"
        :title="$t('FLOWS.DELETE.TITLE')"
        :message="$t('FLOWS.DELETE.MESSAGE')"
        :message-value="` ${selectedFlow ? selectedFlow.name : ''}?`"
        :confirm-text="$t('FLOWS.DELETE.YES')"
        :reject-text="$t('FLOWS.DELETE.NO')"
      />
    </template>
  </SettingsLayout>
</template>
